<script lang="ts">
	import type { ISbStoryData } from '@storyblok/js';
	import type { SbColor } from '../types';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import StarRating from '$lib/review/starRating.svelte';
	import { getYotpoPreview, type YotpoPreview } from '$lib/review/preview';

	interface Props {
		content: ISbStoryData<{
			bg_color: SbColor;
			headline: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let ratings = $state<YotpoPreview>();

	async function getRatings() {
		ratings = await getYotpoPreview();
	}

	$effect.pre(() => {
		getRatings();
	});
</script>

{#snippet wreath(className?: string)}
	<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 47 72" class={className}
		><mask id="a" x="0" y="-3" maskUnits="userSpaceOnUse" style="mask-type:alpha"
			><path
				fill="#fff"
				d="M30.555 64.225c-2.23-3.415-7.156-4.756-10.997-2.995 2.23 3.415 7.156 4.756 10.997 2.995ZM32.326 61.667c3.553-2.15 4.272-6.477 1.606-9.66-3.553 2.15-4.272 6.477-1.606 9.66ZM25.15 54.765c4.001-1.495 5.68-5.58 3.75-9.118-4 1.495-5.68 5.579-3.75 9.118ZM18.415 47.864c4.32-.704 6.99-4.34 5.958-8.116-4.32.704-6.99 4.34-5.958 8.116ZM15.217 37.694c4.398.73 8.474-1.736 9.1-5.508-4.397-.73-8.473 1.736-9.1 5.508ZM17.065 27.383c3.77 2.192 8.733 1.41 11.083-1.745-3.77-2.192-8.734-1.41-11.083 1.745ZM22.435 19.2c2.934 3.008 8.02 3.508 11.354 1.117-2.933-3.007-8.019-3.507-11.354-1.116ZM21.595 56.368c-1.908-3.547-6.71-5.227-10.721-3.751 1.908 3.547 6.71 5.227 10.721 3.75ZM15.69 47.852c-.329-3.841-4.188-6.882-8.618-6.79.328 3.842 4.188 6.882 8.617 6.79ZM12.882 37.192c1.159-3.654-1.38-7.58-5.67-8.765-1.157 3.654 1.382 7.58 5.67 8.765ZM14.324 26.07c2.742-2.901 2.13-7.298-1.364-9.817-2.742 2.901-2.13 7.298 1.364 9.817ZM20.317 17.738c3.687-1.98 4.67-6.258 2.192-9.55-3.687 1.98-4.669 6.258-2.192 9.55ZM27.33 14.838a6.995 6.995 0 0 0 8.446-5.146 6.995 6.995 0 0 0-8.446 5.146Z"
			/></mask
		><g mask="url(#a)"
			><path
				fill="url(#b)"
				d="M0 0h36.51v66.21H0z"
				transform="scale(-1 1) rotate(-9.435 18.71 286.994)"
			/></g
		><defs
			><linearGradient
				id="b"
				x1="18.255"
				x2="18.255"
				y1="0"
				y2="66.21"
				gradientUnits="userSpaceOnUse"
				><stop stop-color="#FEDE60" /><stop offset="1" stop-color="#ECB200" /></linearGradient
			></defs
		></svg
	>
{/snippet}

<section
	id="hero"
	class="bg-{content.bg_color} transition-padding duration-400"
	style="padding-top: var(--full-header-height)"
>
	<PageGrid16 margin class="py-5">
		<div class="col-span-full flex items-center justify-center">
			<div class="h-full">
				{@render wreath('h-full')}
			</div>
			<div class="flex flex-col text-center">
				<StarRating theme="brown-gradient" score={5} starClass="w-3 h-3" />
				<div
					class={[
						'mt-2 font-rhymes text-[32px]/[112%] tracking-[-.32px] text-brown-1',
						{
							'animate-pulse blur-md': ratings === undefined
						}
					]}
				>
					{ratings?.averageScore ?? 4.85}
				</div>
				<div
					class={[
						'text-[14px]/[100%] text-brown-4',
						{
							'animate-pulse blur-md': ratings === undefined
						}
					]}
				>
					{(ratings?.totalReviews ?? 1832).toLocaleString()} reviews
				</div>
			</div>
			<div class="h-full">
				{@render wreath('transform scale-x-[-1] h-full')}
			</div>
		</div>
		<h1
			class="col-span-full mt-6 text-center font-rhymes text-[44px]/[104%] tracking-[-.44px] text-brown-1 lg:col-span-8 lg:col-start-5 lg:text-[68px]/[104%] lg:tracking-[-.68px]"
		>
			{content.headline}
		</h1>
	</PageGrid16>
</section>
